import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, query, orderByChild, equalTo, get } from 'firebase/database';
import { db } from '../../../../FirebaseConfig';
import { SelectDateRange } from './Dashboard_components/SelectDateRange';
import { filterDataByDateRange } from './Analitics_functions/filterDataByDateRange';
import chart from './Charts_components';
import './Dashboard.css';
import Toast from '../../../common/Toast';
import OrganizationSelect from './Dashboard_components/OrganizationSelect';
import SelectQueues from './Dashboard_components/SelectQueues';
import StatsCardsBlock from './Dashboard_components/StatsCardsBlock';
import {
  averageWaitTimeForQ,
  countGuestStatuses,
} from './Analitics_functions/Analitics';
import {getUsersQueues} from '../../../../api/queue';
import { Spinner } from 'react-bootstrap';
import { mockUser, mockUsersQueues, mockUsersOrgs } from './mockData';
import { getLocalISODate } from './Analitics_functions/getLocalIsoDate';

const Dashboard = () => {
  const { BarChart, LineChart } = chart;

  const navigate = useNavigate();
  const [usersQueues, setUsersQueues] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [selectedQueueForBarChart, setSelectedQueueForBarChart] = useState([
    'all',
  ]);
  const [selectedQueueForStats, setSelectedQueueForStats] = useState(['all']);
  const [dateRange, setDateRange] = useState({
    startDate: getLocalISODate(new Date()),
    endDate: getLocalISODate(
      new Date(new Date().setDate(new Date().getDate() + 1))
    ),
  });
  const [user, setUser] = useState(null);
  const [ifWithData, setIfWithData] = useState(true);
  const [queueOptions, setQueueOptions] = useState([
    { value: 'all', label: 'All queues' },
  ]);
  const [dataStats, setDataStats] = useState({
    guestsServed: 0,
    averageWaitTime: 0,
    canceledGuests: 0,
    waitingGuests: 0,
  });
  const [usersOrgs, setUsersOrgs] = useState(null);
  const [orgsOptions, setOrgsOptions] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('all');
  
  const useMock = JSON.parse(localStorage.getItem('useMock')); // Set to true for testing with mock data

useEffect(() => {
  // Mock data for testing
  if (useMock) {
    setUsersQueues(mockUsersQueues);
    setUsersOrgs(mockUsersOrgs);
    setDateRange({
      startDate: '2024-04-01',
      endDate: '2024-04-02',
    });
    setCheckingAuth(false);
    return;
  }

  // production code
  const user = localStorage.getItem('user')
    ? JSON.parse(localStorage.getItem('user'))
    : null;
  setUser(user);
  setCheckingAuth(false);
}, []);

  useEffect(() => {
    if (!user?.uid || useMock) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const userQueues = await getUsersQueues(user.uid);
        setUsersQueues(userQueues);

        if (!selectedQueueForBarChart && Object.keys(userQueues).length > 0) {
          setSelectedQueueForBarChart(Object.keys(userQueues)[0]);
        }

        const orgsSnapshot = await get(
          query(
            ref(db, 'organizations'),
            orderByChild('ownerId'),
            equalTo(user.uid)
          )
        );
        const orgs = orgsSnapshot.val() || {};
        setUsersOrgs(orgs);
      } catch (error) {
        console.error('Error fetching data:', error);
      }finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user]);

  useEffect(() => {
    if (!usersOrgs) return;
    setOrgsOptions(
      Object.entries(usersOrgs).map(([orgId, org]) => ({
        value: orgId,
        label: org.orgName || 'undefined',
      }))
    );
  }, [usersOrgs]);

  useEffect(() => {
    setSelectedQueueForBarChart(['all']);
    setSelectedQueueForStats(['all']);
  }, [selectedOrganization, ifWithData]);

  const filterQueuesByDateRange = useCallback(() => {
    setIsLoading(true);
    const filteredByDate = filterDataByDateRange(
      usersQueues,
      dateRange.startDate,
      dateRange.endDate,
      ifWithData
    );
    if (selectedOrganization !== 'all' && selectedOrganization) {
      let filteredByOrg;
      switch (selectedOrganization) {
        case 'none':
          filteredByOrg = Object.entries(filteredByDate).reduce(
            (acc, [key, val]) => {
              if (!val.organizationId) {
                acc[key] = val;
              }
              return acc;
            },
            {}
          );
          break;
        default:
          filteredByOrg = Object.entries(filteredByDate).reduce(
            (acc, [key, val]) => {
              if (val.organizationId === selectedOrganization) {
                acc[key] = val;
              }
              return acc;
            },
            {}
          );
      }
      setFilteredData(filteredByOrg);
      setIsLoading(false);
      return;
    }
    setFilteredData(filteredByDate);
    setIsLoading(false);
  }, [usersQueues, dateRange, ifWithData, selectedOrganization]);

  useEffect(() => {
    if (usersQueues) {
      filterQueuesByDateRange();
    }
  }, [usersQueues, dateRange, ifWithData, selectedOrganization]);

  useEffect(() => {
    if (filteredData) {
      const options = Object.entries(filteredData).map(([queueId, queue]) => ({
        value: queueId,
        label: queue.queueName || queue.name || 'undefined',
      }));
      setQueueOptions([{ value: 'all', label: 'All queues' }, ...options]);
    }
  }, [filteredData]);

  const handleQueueSelectionForStats = useCallback((selectedOptions) => {
    if (selectedOptions.length === 0) setSelectedQueueForStats([]);
    const queuesForStats = selectedOptions.map((option) => option.value);
    setSelectedQueueForStats(queuesForStats);
  }, []);

  useEffect(() => {
    const stats = getStatsForSelectedQueue();
    setDataStats(stats);
  }, [selectedQueueForStats, filteredData]);

  const handleOrganizationSelection = (selectedOption) => {
    setSelectedOrganization(selectedOption ? selectedOption.value : 'all');
  };

  const onIfWithDataHandler = (e) => {
    setIfWithData(e.target.checked);
  };

  const getStatsForSelectedQueue = useCallback(() => {
    if (!filteredData)
      return {
        guestsServed: 0,
        averageWaitTime: 0,
        canceledGuests: 0,
        waitingGuests: 0,
      };

    let queuesIDs = [];
    if (selectedQueueForStats.includes('all')) {
      queuesIDs = Object.keys(filteredData);
    } else if (selectedQueueForStats.length > 0) {
      queuesIDs = Object.keys(filteredData).filter((id) =>
        selectedQueueForStats.includes(id)
      );
    }

    const aveWaitTimesForAllQueuesForAllDays = queuesIDs
      .map((id) => {
        const queue = filteredData[id];

        const averageWaitTimeForQforEachDay = Object.entries(queue?.daily_stats)
          .map((dayDataArray) => {
            const dayData = dayDataArray[1];
            if (dayData && dayData.guests) {
              const result = averageWaitTimeForQ(dayData);
              return result;
            }
          })
          .filter((v) => v > 0);

        const averageWaitTimeForQForAllDays =
          averageWaitTimeForQforEachDay.length > 0
            ? averageWaitTimeForQforEachDay.reduce((sum, t) => {
                if (t > 0) {
                  return (sum += t);
                } else {
                  return sum;
                }
              }, 0) / averageWaitTimeForQforEachDay.length
            : 0;

        return averageWaitTimeForQForAllDays;
      })
      .filter((v) => v > 0);

    let stats = {
      guestsServed: 0,
      averageWaitTime: Math.ceil(
        aveWaitTimesForAllQueuesForAllDays.filter(v => v > 0).reduce((sum, t) => {
          if (t > 0) {
            return (sum += t);
          } else {
            return sum;
          }
        }, 0) / aveWaitTimesForAllQueuesForAllDays.length
      ),
      canceledGuests: 0,
      waitingGuests: 0,
    };

    stats = queuesIDs.reduce(
      (acc, queue) => {
        const curQ = filteredData[queue];
        if (curQ && curQ.daily_stats) {
          const statuses = countGuestStatuses(curQ.daily_stats);
          acc.guestsServed += statuses.guestsServed;
          acc.canceledGuests += statuses.canceledGuests;
          acc.waitingGuests += statuses.waitingGuests;
        }
        return acc;
      },
      {
        guestsServed: 0,
        averageWaitTime: stats.averageWaitTime || 0,
        canceledGuests: 0,
        waitingGuests: 0,
      }
    );

    return stats;
  }, [filteredData, selectedQueueForStats]);

  if (checkingAuth) {
    return (
      <div className="spinnerWrapper">
        <Spinner animation="border" className="customSpinner" />
        <span>Loading Page...</span>
      </div>
    );
  }
  if (!user && !useMock)
    return (
      <Toast
        title="Temporary warning!"
        subTitle="Please sign up to work on this page."
      />
    );
  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <h4>Analytics Dashboard</h4>
        <div className="filter-container">
          <OrganizationSelect
            organizations={orgsOptions}
            onChangeHandler={handleOrganizationSelection}
          />
          <SelectDateRange
            onFilterChange={(startDate, endDate) =>
              setDateRange({ startDate, endDate })
            }
          />
        </div>
        {isLoading && <p>Loading...</p>}
        {!isLoading &&
          usersQueues &&
          filteredData &&
          Object.keys(usersQueues).length > 0 && (
            <>
              <SelectQueues
                queuesList={queueOptions}
                selectedQueues={selectedQueueForStats}
                withData={ifWithData}
                handleQueueSelectionForStats={handleQueueSelectionForStats}
                onIfWithDataHandler={onIfWithDataHandler}
              />
              <StatsCardsBlock
                totalSelectedValue={
                  selectedQueueForStats.includes('all')
                    ? Object.entries(filteredData).length
                    : selectedQueueForStats.length
                }
                dataStatsProp={dataStats}
              />
              <hr />

              <div className="line-chart">
                {filteredData && (
                  <div className="line-chart-wrap">
                    <LineChart
                      data={filteredData}
                      selectedQueues={
                        selectedQueueForStats.includes('all')
                          ? Object.keys(usersQueues)
                          : selectedQueueForStats
                      }
                      dateRange={dateRange}
                    />
                  </div>
                )}
              </div>

              <hr />

              <div className="bar-chart">
                {filteredData && (
                  <div className="line-chart-wrap">
                  <BarChart
                    data={filteredData}
                    stats={dataStats}
                    selectedQueue={
                      selectedQueueForStats.includes('all')
                        ? Object.keys(usersQueues)
                        : selectedQueueForStats
                    }
                    dateRange={dateRange}
                  />
                  </div>
                )}
              </div>
            </>
          )}

        {!isLoading &&
          (!usersQueues ||
            !filteredData ||
            Object.keys(usersQueues).length === 0) && (
            <p>
              No data available. Analytics will appear once the queue is
              populated. Start creating your queue by{' '}
              <button onClick={() => navigate('/dashboard/queue/create')}>
                Click here
              </button>
            </p>
          )}
      </div>
    </div>
  );
};

export default Dashboard;
